import type { PaymentMethod } from '@/types/checkout';
import type { PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';

interface KlarnaParams {
  paymentMethodId: string;
  paymentMethodDetails: {
    id: string;
    object: string;
    allow_redisplay: string;
    klarna: Record<string, never>;
    billing_details: {
      address: {
        city: string | null;
        country: string | null;
        line1: string | null;
        line2: string | null;
        postal_code: string | null;
        state: string | null;
      };
      email: string | null;
      name: string | null;
      phone: string | null;
      tax_id: null;
    };
    created: number;
    customer: string | null;
    livemode: boolean;
    type: string;
  };
  shouldSaveCard: boolean;
  fraudDetection: {
    forterTokenCookie: string;
  };
}

export interface StripeKlarnaRequest {
  paymentProvider: string;
  receipt_email: string;
  params: KlarnaParams;
  paymentMethodDisplayName: string;
  paymentMethodId: string;
}

// Helper function to get Forter token from cookies
const getForterTokenFromCookie = (): string => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'forterToken' || name === '_ftr') {
      return value;
    }
  }
  return '';
};

export const buildStripeKlarnaPayRequest = (
  paymentMethod: StripePaymentMethod,
  providerSettings: PaymentMethod,
  email: string
): StripeKlarnaRequest => {
  const params: KlarnaParams = {
    paymentMethodId: paymentMethod.id,
    paymentMethodDetails: {
      id: paymentMethod.id,
      object: 'payment_method',
      allow_redisplay: 'unspecified',
      klarna: {},
      billing_details: {
        address: {
          city: paymentMethod.billing_details?.address?.city || null,
          country: paymentMethod.billing_details?.address?.country || null,
          line1: paymentMethod.billing_details?.address?.line1 || null,
          line2: paymentMethod.billing_details?.address?.line2 || null,
          postal_code: paymentMethod.billing_details?.address?.postal_code || null,
          state: paymentMethod.billing_details?.address?.state || null,
        },
        email: paymentMethod.billing_details?.email || email || null,
        name: paymentMethod.billing_details?.name || null,
        phone: paymentMethod.billing_details?.phone || null,
        tax_id: null,
      },
      created: paymentMethod.created,
      customer: paymentMethod.customer as string | null,
      livemode: paymentMethod.livemode,
      type: 'klarna',
    },
    shouldSaveCard: false,
    fraudDetection: {
      forterTokenCookie: getForterTokenFromCookie(),
    },
  };

  return {
    paymentProvider: 'stripe',
    receipt_email: email || paymentMethod.billing_details?.email || '',
    params,
    paymentMethodDisplayName: 'Klarna',
    paymentMethodId: providerSettings.id,
  };
};
